import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, MapPin, Phone, Package, User, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { TYPE_STYLES, STATUS_STYLES, fmtTime, ymd, hm } from "@/lib/schedule";

const STATUSES = ["Scheduled", "In Progress", "Completed", "Cancelled"];
const lbl = "text-xs font-semibold uppercase tracking-wider text-slate-500";

const Info = ({ ev }) => (
  <div className="space-y-1.5 text-sm text-slate-700">
    <div className="font-semibold text-slate-900 text-base" data-testid="event-detail-company">{ev.company_name || "—"}</div>
    <div className="text-xs text-slate-500">{new Date(ev.start).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })} · {fmtTime(ev.start)} – {fmtTime(ev.end)}</div>
    {ev.site_address && <div className="flex gap-2"><MapPin className="w-4 h-4 text-slate-400 shrink-0" />{ev.site_address}</div>}
    {(ev.contact_person || ev.contact_phone) && (
      <div className="flex gap-2"><Phone className="w-4 h-4 text-slate-400 shrink-0" />{ev.contact_person}
        {ev.contact_phone && <a href={`tel:${ev.contact_phone}`} className="text-indigo-700 hover:underline" data-testid="event-detail-phone">{ev.contact_phone}</a>}
      </div>
    )}
    {ev.machine_model && <div className="flex gap-2"><Package className="w-4 h-4 text-slate-400 shrink-0" />{ev.machine_model}{ev.serial_number ? ` · S/N ${ev.serial_number}` : ""}</div>}
    <div className="flex gap-2"><User className="w-4 h-4 text-slate-400 shrink-0" /><span data-testid="event-detail-engineer">{ev.engineer_name || "Unassigned"}</span></div>
  </div>
);

export const EventDetailDialog = ({ event, meta, guard, onClose, onChanged }) => {
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!event) return;
    setNotes(event.notes || "");
    setDate(ymd(new Date(event.start))); setFrom(hm(new Date(event.start))); setTo(hm(new Date(event.end)));
  }, [event]);
  if (!event) return null;
  const ev = event;
  const closed = ev.status === "Cancelled";

  const done = (res, msg) => { if (res) { toast.success(msg); onChanged?.(); } };
  const setStatus = async (status) => {
    setBusy(true);
    const res = await guard.run((allow) => api.patch(`/schedule/events/${ev.id}/status${allow ? "?allow_clash=true" : ""}`, { status, notes }));
    setBusy(false);
    done(res, `Marked ${status}`);
  };
  const reschedule = async () => {
    const start = new Date(`${date}T${from}:00`); const end = new Date(`${date}T${to}:00`);
    if (!(end > start)) { toast.error("End time must be after start time"); return; }
    const res = await guard.run((allow) => api.patch(`/schedule/events/${ev.id}/reschedule${allow ? "?allow_clash=true" : ""}`, { start: start.toISOString(), end: end.toISOString() }));
    done(res, "Rescheduled");
  };
  const reassign = async (engineer_id) => {
    const res = await guard.run((allow) => api.patch(`/schedule/events/${ev.id}/assign${allow ? "?allow_clash=true" : ""}`, { engineer_id }));
    done(res, "Engineer reassigned");
  };

  return (
    <Dialog open={!!event} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={`text-[11px] font-semibold uppercase tracking-wider border rounded px-1.5 py-0.5 ${TYPE_STYLES[ev.type]?.chip}`}>{ev.type}</span>
            <span className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${STATUS_STYLES[ev.status]}`} data-testid="event-detail-status">{ev.status}</span>
          </DialogTitle>
        </DialogHeader>
        <Info ev={ev} />
        <Link to={ev.source_link} className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline font-medium" data-testid="event-open-source-link">
          Open {ev.type === "Demo" ? "demo booking" : ev.type} <ExternalLink className="w-3.5 h-3.5" />
        </Link>
        {ev.type === "Demo" && (
          <Link to={`/bookings?edit=${ev.source_id}`} className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-indigo-700 font-medium ml-4" data-testid="event-edit-booking-link">
            <Pencil className="w-3.5 h-3.5" /> Edit booking
          </Link>
        )}

        <div className="border-t border-slate-100 pt-3 space-y-2">
          <Label className={lbl}>Status & completion notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What happened on site?" data-testid="event-notes-input" />
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <Button key={s} size="sm" disabled={busy} variant={ev.status === s ? "default" : "outline"}
                className={ev.status === s ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                onClick={() => setStatus(s)} data-testid={`event-status-btn-${s.replace(/\s+/g, "-")}`}>{s}</Button>
            ))}
          </div>
        </div>

        {!closed && (
          <div className="border-t border-slate-100 pt-3 space-y-2">
            <Label className={lbl}>Reschedule visit</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Input type="date" className="col-span-2 sm:col-span-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="event-date-input" />
              <Input type="time" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="event-start-input" />
              <Input type="time" value={to} onChange={(e) => setTo(e.target.value)} data-testid="event-end-input" />
            </div>
            <Button size="sm" variant="outline" onClick={reschedule} data-testid="event-reschedule-btn">Update time</Button>
          </div>
        )}

        {!closed && meta?.can_assign_others && (
          <div className="border-t border-slate-100 pt-3">
            <Label className={lbl}>Assigned engineer</Label>
            <Select value={ev.engineer_id} onValueChange={reassign}>
              <SelectTrigger className="mt-1" data-testid="event-engineer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta.engineers || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.job_title ? ` · ${u.job_title}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
